import React from 'react';
import { Card } from './ui/card';
import { CheckCircle, Award, Users, Clock } from 'lucide-react';

const AboutSection = () => {
  const achievements = [
    {
      icon: Clock,
      number: "22+",
      label: "Years of Excellence",
      description: "Serving our community since 2003"
    },
    {
      icon: Users,
      number: "1,10,000+",
      label: "Lives Transformed",
      description: "Success stories across all age groups"
    },
    {
      icon: Award,
      number: "80+",
      label: "Industry Awards",
      description: "Recognition for outstanding care"
    }
  ];

  const values = [
    "Patient-centered care philosophy",
    "Evidence-based treatment approaches", 
    "Continuous professional development",
    "Cutting-edge technology integration",
    "Holistic wellness solutions",
    "Compassionate, personalized attention"
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#204080]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium">
                <Award size={16} />
                <span>About Visakha Physiotherapy & Jasti Chiropractic</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] leading-tight font-serif">
                A Legacy of 
                <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
                  Healing Excellence
                </span>
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                For over two decades, Visakha Physiotherapy & Jasti Chiropractic has been at the forefront of innovative 
                healthcare, transforming lives through expert care, compassionate treatment, and 
                unwavering dedication to patient wellness.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Our patient-first philosophy guides every interaction, ensuring that each individual 
                receives personalized attention and evidence-based treatments tailored to their unique 
                needs. We believe in not just treating symptoms, but addressing root causes to achieve 
                lasting wellness and prevention.
              </p>
            </div>

            {/* Values List */}
            <div className="grid grid-cols-1 gap-3">
              {values.map((value, index) => (
                <div key={index} className="flex items-center space-x-3 group">
                  <CheckCircle className="text-[#2E5AAC] group-hover:scale-110 transition-transform duration-200" size={20} />
                  <span className="text-gray-700 group-hover:text-[#204080] transition-colors duration-200">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Achievement Cards */}
          <div className="space-y-8">
            <div className="grid gap-6">
              {achievements.map((achievement, index) => {
                const IconComponent = achievement.icon;
                return (
                  <Card key={index} className="group p-8 bg-white/80 backdrop-blur-sm border border-gray-200 hover:border-[#204080]/30 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
                    <div className="flex items-center space-x-6">
                      <div className="flex-shrink-0">
                        <div className="p-4 bg-gradient-to-br from-[#204080] to-[#2E5AAC] rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <IconComponent size={32} className="text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-3xl font-bold text-[#204080] mb-1">
                          {achievement.number}
                        </div>
                        <div className="text-lg font-semibold text-gray-800 mb-1">
                          {achievement.label}
                        </div>
                        <div className="text-gray-600 text-sm">
                          {achievement.description}
                        </div>
                      </div>
                    </div>
                    
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-[#2E5AAC]/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Card>
                );
              })}
            </div>

            {/* Mission Statement Card */}
            <Card className="p-8 bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white shadow-2xl">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold font-serif">Our Mission</h3>
                <p className="text-lg text-blue-100 leading-relaxed">
                  "To restore movement, relieve pain, and uplift lives through innovative 
                  physiotherapy solutions, compassionate care, and an unwavering commitment 
                  to excellence in every patient interaction."
                </p>
                <div className="pt-4 border-t border-white/20">
                  <div className="text-lg text-blue-200">
                    Trusted by healthcare professionals, athletes, and families across the region
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;